import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Activity, Bot } from "lucide-react";
import { useApi, useInvalidateOn, type AgentRun } from "@ncp/api-client";
import { formatDateTime, useEnumLabel, useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Card, CardContent, CardHeader, CardTitle, EmptyState, Skeleton, StatCard, StatusBadge, cn } from "@ncp/ui";
import { AgentRunPanel } from "@ncp/assistant-sdk";

export function AgentsTab({ caseId }: { caseId: string }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const enumLabel = useEnumLabel();
  const api = useApi();
  useInvalidateOn(["agent.run", "agent.step"], [["case-runs", caseId]]);
  const runs = useQuery({ queryKey: ["case-runs", caseId], queryFn: () => api.get<AgentRun[]>("/api/ai/runs", { caseId, take: 100 }), refetchInterval: 5000 });
  const [selected, setSelected] = useState<string | null>(null);
  if (runs.isLoading) return <Skeleton className="h-96" />;
  const items = runs.data ?? [];
  const active = selected ?? items[0]?.id ?? null;
  const tokens = items.reduce((a, r) => a + r.inputTokens + r.outputTokens, 0);
  const pii = items.reduce((a, r) => a + (r.piiTokensRedacted ?? 0), 0);

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard label={t("backoffice:workspace.agentRuns")} value={items.length} icon={Bot} />
        <StatCard label={t("ai.tokens")} value={tokens} icon={Activity} tone="sea" />
        <StatCard label={t("backoffice:workspace.piiRedacted")} value={pii} icon={Activity} tone="neutral" footer={t("backoffice:workspace.piiHint")} />
      </div>
      {items.length === 0 ? <EmptyState icon={Bot} title={t("backoffice:workspace.noRuns")} /> : (
        <div className="grid gap-6 xl:grid-cols-[400px_1fr]">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">{t("backoffice:workspace.agentRuns")}</CardTitle></CardHeader>
            <CardContent className="max-h-[70vh] space-y-2 overflow-y-auto scrollbar-thin" tabIndex={0} role="region" aria-label={t("backoffice:workspace.agentRuns")}>
              {items.map((r) => (
                <button key={r.id} type="button" onClick={() => setSelected(r.id)} className={cn("w-full rounded-xl border p-3 text-start", r.id === active ? "border-aegold-400 bg-aegold-50/60" : "border-sand-200 hover:bg-sand-50")}>
                  <div className="flex items-center gap-2"><p className="flex-1 truncate text-sm font-medium">{locale === "ar" ? (r.agentNameAr ?? r.agentName) : r.agentName}</p><StatusBadge status={r.status === "Succeeded" ? "Approved" : r.status === "Failed" ? "Rejected" : "UnderReview"} label={enumLabel("runStatus", r.status)} /></div>
                  <p className="text-xs text-muted-foreground">{formatDateTime(r.startedAt, locale)} · {enumLabel("triggerSource", r.triggerSource)}{r.workflowState ? ` · ${enumLabel("workflowState", r.workflowState)}` : ""}</p>
                  {r.toolCalls || r.retrievalCount ? (
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      {[r.toolCalls ? t("backoffice:workspace.runTools", { count: r.toolCalls }) : null, r.retrievalCount ? t("backoffice:workspace.runSources", { count: r.retrievalCount }) : null].filter(Boolean).join(" · ")}
                    </p>
                  ) : null}
                  {r.error ? <Badge size="sm" variant="danger" className="mt-1">{t("backoffice:workspace.runFailed")}</Badge> : null}
                </button>
              ))}
            </CardContent>
          </Card>
          {active ? <AgentRunPanel runId={active} /> : null}
        </div>
      )}
    </div>
  );
}
